/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { message } from "antd";
import { clinicSettingsService } from "../../../services/clinicSettingsService";
import ConfirmModal from "../../../components/common/ConfirmModal";
import Breadcrumb from '../../../components/ui/Breadcrumb';

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

const Setting = () => {
  const fileInputRef = useRef(null);
  const logoPreviewRef = useRef(null);

  const [form, setForm] = useState({
    clinic_name: "",
    address: "",
    phone: "",
    rnc: "",
    dgii_authorization_number: "",
    economic_activity_code: "",
    fiscal_establishment: "",
    fiscal_point_of_sale: "",
    ncf_warning_threshold: 50,
    ecf_provider: "",
    ecf_api_key: "",
    ecf_api_secret: "",
    ecf_webhook_url: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [currentLogoUrl, setCurrentLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [errors, setErrors] = useState({});
  const [fileError, setFileError] = useState("");
  const [showDeleteLogoConfirm, setShowDeleteLogoConfirm] = useState(false);

  useEffect(() => {
    logoPreviewRef.current = logoPreview;
  }, [logoPreview]);

  useEffect(() => {
    fetchSettings();

    return () => {
      if (logoPreviewRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(logoPreviewRef.current);
      }
    };
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settings = await clinicSettingsService.getSettings();
      setForm({
        clinic_name: settings.clinic_name || "",
        address: settings.address || "",
        phone: settings.phone || "",
        rnc: settings.rnc || "",
        dgii_authorization_number: settings.dgii_authorization_number || "",
        economic_activity_code: settings.economic_activity_code || "",
        fiscal_establishment: settings.fiscal_establishment || "",
        fiscal_point_of_sale: settings.fiscal_point_of_sale || "",
        ncf_warning_threshold: settings.ncf_warning_threshold ?? 50,
        ecf_provider: settings.ecf_provider || "",
        ecf_api_key: settings.ecf_api_key || "",
        ecf_api_secret: settings.ecf_api_secret || "",
        ecf_webhook_url: settings.ecf_webhook_url || "",
      });
      if (settings.logo_url) {
        setCurrentLogoUrl(settings.logo_url);
      }
    } catch {
      message.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError("");

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError("Formato no permitido. Use JPEG, PNG, GIF o WebP.");
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileError("El archivo excede el límite de 5MB.");
      return;
    }

    if (logoPreviewRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreviewRef.current);
    }

    const blobUrl = URL.createObjectURL(file);
    logoPreviewRef.current = blobUrl;
    setLogoFile(file);
    setLogoPreview(blobUrl);
  };

  const resetLogoSelection = () => {
    setLogoFile(null);
    if (logoPreviewRef.current?.startsWith("blob:")) {
      URL.revokeObjectURL(logoPreviewRef.current);
      logoPreviewRef.current = null;
    }
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const requestDeleteLogo = () => {
    if (!currentLogoUrl) {
      message.info("No hay logo para eliminar");
      return;
    }
    setShowDeleteLogoConfirm(true);
  };

  const cancelDeleteLogo = () => {
    if (deletingLogo) return;
    setShowDeleteLogoConfirm(false);
  };

  const handleDeleteLogo = () => {
    setDeletingLogo(true);
    clinicSettingsService
      .deleteLogo()
      .then(() => {
        setCurrentLogoUrl(null);
        setLogoFile(null);
        if (logoPreviewRef.current?.startsWith("blob:")) {
          URL.revokeObjectURL(logoPreviewRef.current);
          logoPreviewRef.current = null;
        }
        setLogoPreview(null);
        message.success("Logo eliminado correctamente");
        setShowDeleteLogoConfirm(false);
      })
      .catch(() => {
        message.error("Error al eliminar el logo");
      })
      .finally(() => {
        setDeletingLogo(false);
      });
  };

  const validate = () => {
    const newErrors = {};
    if (!form.clinic_name.trim()) {
      newErrors.clinic_name = "El nombre de la clínica es obligatorio";
    }
    if (form.clinic_name.length > 255) {
      newErrors.clinic_name =
        "El nombre de la clínica no debe exceder 255 caracteres";
    }
    if (form.rnc && !/^\d{9}$|^\d{11}$/.test(form.rnc.replace(/\D/g, ''))) {
      newErrors.rnc = "El RNC debe tener 9 u 11 dígitos numéricos";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setSaving(true);
    try {
      await clinicSettingsService.updateSettings({
        clinic_name: form.clinic_name.trim(),
        address: form.address.trim() || undefined,
        phone: form.phone.trim() || undefined,
        rnc: form.rnc.trim() || undefined,
        dgii_authorization_number: form.dgii_authorization_number.trim() || undefined,
        economic_activity_code: form.economic_activity_code.trim() || undefined,
        fiscal_establishment: form.fiscal_establishment.trim() || undefined,
        fiscal_point_of_sale: form.fiscal_point_of_sale.trim() || undefined,
        ncf_warning_threshold: form.ncf_warning_threshold || undefined,
        ecf_provider: form.ecf_provider || undefined,
        ecf_api_key: form.ecf_api_key || undefined,
        ecf_api_secret: form.ecf_api_secret || undefined,
        ecf_webhook_url: form.ecf_webhook_url || undefined,
      });

      if (logoFile) {
        try {
          const result = await clinicSettingsService.uploadLogo(logoFile);
          setCurrentLogoUrl(result.logo_url);
          setLogoFile(null);
          if (logoPreviewRef.current?.startsWith("blob:")) {
            URL.revokeObjectURL(logoPreviewRef.current);
            logoPreviewRef.current = null;
          }
          setLogoPreview(result.logo_url);
        } catch {
          message.warning(
            "La información se guardó pero hubo un error al subir el logo"
          );
        }
      }

      message.success("Configuración guardada correctamente");
    } catch (err) {
      if (err?.response?.status === 422 && err?.response?.data?.errors) {
        const serverErrors = {};
        const errData = err.response.data.errors;
        Object.keys(errData).forEach((key) => {
          serverErrors[key] = Array.isArray(errData[key])
            ? errData[key][0]
            : errData[key];
        });
        setErrors(serverErrors);
      } else {
        message.error(
          "Error de conexión. Verifique su conexión e intente nuevamente."
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const displayLogo = logoPreview || currentLogoUrl;

  if (loading) {
    return (
      <>
        <div className="page-header">
          <div className="row">
            <div className="col-sm-12">
              <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Configuración' }]} />
            </div>
          </div>
        </div>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Configuración' }]} />
              </div>
            </div>
          </div>

          <div className="settings-menu-links">
            <ul className="nav nav-tabs menu-tabs">
              <li className="nav-item active">
                <Link className="nav-link" to="/settings">
                  General Settings
                </Link>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  Localization
                </span>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  Payment Settings
                </span>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  Email Settings
                </span>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  Social Media Login
                </span>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  Social Links
                </span>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  SEO Settings
                </span>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  Theme Settings
                </span>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  Change Password
                </span>
              </li>
              <li className="nav-item">
                <span className="nav-link">
                  Others
                </span>
              </li>
            </ul>
          </div>

          <div className="row">
            <div className="col-lg-10">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">Información de la Clínica</h5>
                  <p
                    className="text-muted mb-0"
                    style={{ fontSize: "13px" }}
                  >
                    Personalice la información que aparecerá en sus documentos
                    impresos
                  </p>
                </div>
                <div className="card-body pt-0">
                  <form onSubmit={handleSave}>
                    <div className="settings-form">
                      <div className="row">
                        <div className="col-md-4 text-center">
                          <div className="form-group">
                            <p className="settings-label">Logo</p>
                            <div
                              className="upload-images mb-2"
                              style={{
                                minHeight: "150px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px dashed #dee2e6",
                                borderRadius: "8px",
                                padding: "16px",
                                background: "#fafafa",
                                flexDirection: "column",
                              }}
                            >
                              {displayLogo ? (
                                <>
                                  <img
                                    src={displayLogo}
                                    alt="Logo de la clínica"
                                    style={{
                                      maxWidth: "150px",
                                      maxHeight: "150px",
                                      objectFit: "contain",
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                      const errorEl =
                                        e.currentTarget.parentElement?.querySelector(
                                          ".logo-error"
                                        );
                                      if (errorEl)
                                        errorEl.style.display = "block";
                                    }}
                                  />
                                  <div
                                    className="logo-error"
                                    style={{
                                      display: "none",
                                      color: "#dc3545",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Logo no disponible
                                  </div>
                                </>
                              ) : (
                                <div
                                  className="text-muted"
                                  style={{ fontSize: "13px" }}
                                >
                                  <FeatherIcon
                                    icon="image"
                                    style={{
                                      width: 32,
                                      height: 32,
                                      marginBottom: 8,
                                    }}
                                  />
                                  <div>No hay logo cargado</div>
                                </div>
                              )}
                            </div>

                            <div className="settings-btn mb-2">
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                name="logo"
                                id="logo-file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                className="hide-input"
                              />
                              <label htmlFor="logo-file" className="upload">
                                <i className="feather-upload">
                                  <FeatherIcon icon="upload" />
                                </i>
                              </label>
                              {logoFile && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary ms-2"
                                  onClick={resetLogoSelection}
                                >
                                  Cancelar
                                </button>
                              )}
                              {logoFile && (
                                <small
                                  className="d-block mt-1"
                                  style={{ fontSize: "11px" }}
                                >
                                  {logoFile.name}
                                </small>
                              )}
                            </div>

                            {currentLogoUrl && !logoFile && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={requestDeleteLogo}
                                disabled={deletingLogo}
                              >
                                {deletingLogo ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-1"
                                      role="status"
                                    />
                                    Eliminando...
                                  </>
                                ) : (
                                  <>
                                    <i className="feather-trash-2 me-1">
                                      <FeatherIcon icon="trash-2" />
                                    </i>
                                    Eliminar logo
                                  </>
                                )}
                              </button>
                            )}

                            {fileError && (
                              <div
                                className="text-danger mt-1"
                                style={{ fontSize: "12px" }}
                              >
                                {fileError}
                              </div>
                            )}

                            <hr className="mt-4 mb-3" />
                            <h6 style={{ color: "#991b1b" }}>Certificado Digital (e-CF)</h6>
                            <div className="form-group">
                              <label className="settings-label">Certificado .p12/.pfx</label>
                              <input type="file" accept=".p12,.pfx" className="form-control form-control-sm"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  const formData = new FormData();
                                  formData.append('certificate', file);
                                  try {
                                    await clinicSettingsService.uploadCertificate(formData);
                                    message.success('Certificado subido correctamente');
                                  } catch { message.error('Error al subir el certificado'); }
                                }} />
                              <small className="form-text text-muted">Certificado AVANSI para firma digital e-CF. Máximo 10MB.</small>
                            </div>

                            <p className="settings-size mt-2">
                              Tamaño recomendado:{" "}
                              <span>150px x 150px</span>
                            </p>
                            <p className="settings-size">
                              Máximo 5MB. Formatos: JPEG, PNG, GIF, WebP
                            </p>
                          </div>
                        </div>

                        <div className="col-md-8">
                          <div className="form-group">
                            <label>
                              Nombre de la clínica{" "}
                              <span className="star-red">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${errors.clinic_name ? "is-invalid" : ""}`}
                              placeholder="Ej: Centro Médico Avanzado"
                              name="clinic_name"
                              value={form.clinic_name}
                              onChange={handleInputChange}
                              maxLength={255}
                            />
                            {errors.clinic_name && (
                              <div className="invalid-feedback d-block">
                                {errors.clinic_name}
                              </div>
                            )}
                          </div>

                          <div className="form-group">
                            <label>Dirección</label>
                            <input
                              type="text"
                              className={`form-control ${errors.address ? "is-invalid" : ""}`}
                              placeholder="Ej: Av. Independencia 123, Santo Domingo"
                              name="address"
                              value={form.address}
                              onChange={handleInputChange}
                              maxLength={500}
                            />
                            {errors.address && (
                              <div className="invalid-feedback d-block">
                                {errors.address}
                              </div>
                            )}
                          </div>

                          <div className="form-group">
                            <label>Teléfono</label>
                            <input
                              type="text"
                              className={`form-control ${errors.phone ? "is-invalid" : ""}`}
                              placeholder="Ej: 809-555-0100"
                              name="phone"
                              value={form.phone}
                              onChange={handleInputChange}
                              maxLength={50}
                            />
                            {errors.phone && (
                              <div className="invalid-feedback d-block">
                                {errors.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="form-group mb-0 mt-3">
                        <div className="settings-btns">
                          <button
                            type="submit"
                            className="border-0 btn btn-primary btn-gradient-primary btn-rounded me-2"
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-1"
                                  role="status"
                                />
                                Guardando...
                              </>
                            ) : (
                              "Guardar cambios"
                            )}
                          </button>
                        </div>
                          </div>

                          <hr className="mt-4 mb-3" />
                          <h5 className="mb-3" style={{ color: "#991b1b" }}>
                            <i className="fas fa-file-invoice me-2"></i>
                            Configuración Fiscal DGII
                          </h5>

                          <div className="form-group">
                            <label>RNC</label>
                            <input
                              type="text"
                              className={`form-control ${errors.rnc ? "is-invalid" : ""}`}
                              placeholder="9 u 11 dígitos (ej: 130123456)"
                              name="rnc"
                              value={form.rnc}
                              onChange={handleInputChange}
                              maxLength={11}
                            />
                            {errors.rnc && (
                              <div className="invalid-feedback d-block">{errors.rnc}</div>
                            )}
                            <small className="form-text text-muted">
                              Déjelo vacío si la clínica no tiene RNC (solo emitirá recibos REC-)
                            </small>
                          </div>

                          <div className="form-group">
                            <label>No. Autorización DGII</label>
                            <input type="text" className="form-control" placeholder="Ej: DGII-AUTH-2026-001"
                              name="dgii_authorization_number" value={form.dgii_authorization_number} onChange={handleInputChange} maxLength={50} />
                          </div>

                          <div className="form-group">
                            <label>Código de Actividad Económica</label>
                            <input type="text" className="form-control" placeholder="Ej: 85110"
                              name="economic_activity_code" value={form.economic_activity_code} onChange={handleInputChange} maxLength={20} />
                          </div>

                          <div className="row">
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>Establecimiento Fiscal</label>
                                <input type="text" className="form-control" placeholder="001"
                                  name="fiscal_establishment" value={form.fiscal_establishment} onChange={handleInputChange} maxLength={50} />
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="form-group">
                                <label>Punto de Venta</label>
                                <input type="text" className="form-control" placeholder="001"
                                  name="fiscal_point_of_sale" value={form.fiscal_point_of_sale} onChange={handleInputChange} maxLength={20} />
                              </div>
                            </div>
                          </div>

                          <div className="form-group">
                            <label>Umbral de alerta NCF</label>
                            <input type="number" className="form-control" min="1" max="999"
                              name="ncf_warning_threshold" value={form.ncf_warning_threshold}
                              onChange={handleInputChange} />
                            <small className="form-text text-muted">
                              Muestra alerta cuando queden menos de este número de comprobantes en una secuencia (default: 50)
                            </small>
                          </div>

                          <hr className="mt-4 mb-3" />
                          <h6 className="mb-3" style={{ color: "#0d6efd" }}>
                            <i className="fas fa-cloud-upload-alt me-2"></i>
                            Proveedor e-CF (Comprobante Fiscal Electrónico)
                          </h6>

                          <div className="form-group">
                            <label>Proveedor e-CF</label>
                            <select className="form-select" name="ecf_provider"
                              value={form.ecf_provider} onChange={handleInputChange}>
                              <option value="">Ninguno (solo NCF)</option>
                              <option value="mock">Mock (Desarrollo)</option>
                            </select>
                            <small className="form-text text-muted">
                              Seleccione el proveedor DGII certificado para firma digital y transmisión
                            </small>
                          </div>

                          <div className="form-group">
                            <label>API Key</label>
                            <input type="password" className="form-control" placeholder="••••••••"
                              name="ecf_api_key" value={form.ecf_api_key} onChange={handleInputChange}
                              autoComplete="off" />
                            <small className="form-text text-muted">Credencial de acceso al proveedor e-CF</small>
                          </div>

                          <div className="form-group">
                            <label>API Secret</label>
                            <input type="password" className="form-control" placeholder="••••••••"
                              name="ecf_api_secret" value={form.ecf_api_secret} onChange={handleInputChange}
                              autoComplete="off" />
                            <small className="form-text text-muted">Secreto de acceso al proveedor e-CF</small>
                          </div>

                          <div className="form-group">
                            <label>Webhook URL</label>
                            <input type="text" className="form-control" placeholder="https://webhook.example.com/ecf"
                              name="ecf_webhook_url" value={form.ecf_webhook_url} onChange={handleInputChange}
                              maxLength={500} />
                            <small className="form-text text-muted">
                              URL donde el proveedor notificará cambios de estado (opcional)
                            </small>
                          </div>
                        </div>
                  </form>
</div>
          </div>
        </div>
      </div>

      <ConfirmModal
        show={showDeleteLogoConfirm}
        title="Eliminar logo"
        body="¿Eliminar el logo actual? Esta acción no se puede deshacer."
        confirmLabel="Eliminar"
        confirmVariant="danger"
        loading={deletingLogo}
        onConfirm={handleDeleteLogo}
        onCancel={cancelDeleteLogo}
      />
    </>
  );
};

export default Setting;
